import React from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Creatable from "react-select/lib/Creatable";
import { useSelector, useDispatch } from "react-redux";
import { buildOptions } from "../utils";
import { uniq } from "lodash";
import useFetch from "../fetch";
import { sendStaffAppMetaData } from "../staffApp/eventTriggers";
import DatePicker from "react-datepicker";
import moment from "moment";
import Select from "react-select";

function FamilyMemberFormModel() {
  const props = useSelector((state) => {
    return {
      show: state.ui.familyMemberModal.show,
      familyMember: state.ui.familyMemberModal.familyMember,
      action: state.ui.familyMemberModal.action,
      organizationName: state.organization.name,
      resident: state.resident,
      isStaffApp: state.config.isStaffApp,
      familyMemberTabDefaultMetaData: state.config.tab_default_meta_data.find(
        (tab) => tab.detail == "family_members"
      ),
    };
  });
  const dispatch = useDispatch();
  const fetch = useFetch();
  const closeModal = () =>
    dispatch({
      type: "CLOSE_RESOURCE_MODAL",
      payload: { resourceName: "familyMember" },
    });
  const updateField = (name, value) =>
    dispatch({
      type: "UPDATE_RESOURCE_FIELD",
      payload: { resourceName: "familyMember", name, value },
    });
  const deleteFamilyMember = (familyMember) =>
    dispatch({ type: "DELETE_FAMILY_MEMBER", payload: { familyMember } });
  const setDefaultStaffAppMetaData = () =>
    sendStaffAppMetaData(props.familyMemberTabDefaultMetaData);

  const updateResidentFamilyMembersAction = () => {
    return (dispatch, getState) => {
      const state = getState();
      return fetch(
        `/organizations/${state.organization.name}/residents/${state.resident.id}.json`,
        {
          method: "PATCH",
          headers: {'Content-Type': 'application/json'},
          data: JSON.stringify({
            resident: {
              family_members_attributes: [state.ui.familyMemberModal.familyMember]
            },
          })
        }
      ).then((data) => {
        dispatch({
          type: "UPDATE_FAMILY_MEMBER",
          payload: { familyMember: data.family_members },
        });
      });
    };
  };

  const updateResidentFamilyMembers = (familyMember) => {
    const { organizationName, resident } = props;
    return fetch(
      `/organizations/${organizationName}/residents/${resident.id}.json`,
      {
        method: "PATCH",
        headers: {'Content-Type': 'application/json'},
        data: JSON.stringify({
          resident: {
            family_members_attributes: [familyMember]
          },
        })
      }
    );
  };

  const removeFamilyMember = () => {
    updateResidentFamilyMembers({
      id: props.familyMember.id,
      _destroy: true,
    }).then(() => {
      deleteFamilyMember(props.familyMember);
      if (props.isStaffApp) alert("Family Members successfully deleted.");
      setDefaultStaffAppMetaData();
      closeModal();
    });
  };

  const saveFamilyMember = () => {
    dispatch(updateResidentFamilyMembersAction()).then((data) => {
      if (props.isStaffApp) alert("Family Members changes saved.");
      setDefaultStaffAppMetaData();
      closeModal();
    });
  };

  const { name, birthdate, relationship, gender, living_together } = props.familyMember;
  const relationshipOptions = buildOptions(
    uniq([
      "Father",
      "Mother",
      "Spouse",
      "Biological Child",
      "Adopted Child",
      "Child of Guardianship",
      "Uncle",
      "Aunt",
      "Grandmother",
      "Grandfather",
      relationship,
    ])
  );
  const relationshipValue = relationshipOptions.find(
    (option) => option.value === relationship
  );
  const genderOptions = buildOptions(
    uniq([
      "Male",
      "Female",
      "Trans",
      "Non-Binary",
      gender,
    ])
  );
  const genderValue = genderOptions.find(
    (option) => option.value === gender
  );

  React.useEffect(() => {
    let saveFamilyMemberFunction = (e) => {
      saveFamilyMember();
    };
    document.addEventListener("saveFamilyMember", saveFamilyMemberFunction);
    return () =>
      document.removeEventListener(
        "saveFamilyMember",
        saveFamilyMemberFunction
      );
  }, []);

  const livingTogetherOptions = buildOptions([[true, 'Yes'], [false, 'No']]);
  const livingTogetherValue = livingTogetherOptions.find((option) => option.value === living_together);

  return (
    <Modal
      animation={!props.isStaffApp}
      show={props.show}
      onHide={() => closeModal()}
    >
      {!props.isStaffApp && (
        <Modal.Header closeButton>
          <Modal.Title>Family Member</Modal.Title>
        </Modal.Header>
      )}
      <Modal.Body className="p-4">
        <Form.Group controlId="familyMemberName">
          <Form.Label>Name</Form.Label>
          <Form.Control
            value={name}
            onChange={(e) => {
              updateField("name", e.target.value);
            }}
          />
        </Form.Group>
        <Form.Group controlId="familyMemberBirthdate">
          <Form.Label>Birthdate</Form.Label>
          <DatePicker
            selected={birthdate ? moment(birthdate) : null}
            className="form-control"
            onChange={(date) => {
              updateField("birthdate", date ? date.format("YYYY-MM-DD") : null);
            }}
          />
        </Form.Group>
        <Form.Group controlId="familyMemberRelationship">
          <Form.Label>Relation</Form.Label>
          <Creatable
            options={relationshipOptions}
            value={relationshipValue}
            onChange={(option) => {
              updateField("relationship", option.value);
            }}
          />
        </Form.Group>
        <Form.Group controlId="familyMemberGender">
          <Form.Label>Gender</Form.Label>
          <Creatable
            options={genderOptions}
            value={genderValue}
            onChange={(option) => {
              updateField("gender", option.value);
            }}
          />
        </Form.Group>
        <Form.Group controlId="familyMemberLivingTogether">
          <Form.Label>Does family member live with client</Form.Label>
          <Select
            value={livingTogetherValue}
            options={livingTogetherOptions}
            onChange={(option) => {
              updateField("living_together", option.value);
            }}
          />
        </Form.Group>
      </Modal.Body>
      {!props.isStaffApp ? (
        <Modal.Footer>
          {props.action === "edit" && (
            <Button
              variant="outline-danger"
              onClick={() => removeFamilyMember(props.familyMember)}
            >
              Delete
            </Button>
          )}
          <Button variant="info" onClick={() => saveFamilyMember()}>
            {props.action === "edit" ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      ) : (
        <div className="p-4">
          {props.action == "edit" && (
            <Button
              onClick={() => removeFamilyMember(props.familyMember)}
              variant="outline-danger"
              className="w-100"
            >
              Delete
            </Button>
          )}
        </div>
      )}
    </Modal>
  );
}

export default FamilyMemberFormModel;
